import { isPlainObject } from "es-toolkit";
import { useAtomValue, type WritableAtom } from "jotai";
import { atomFamily, atomWithStorage, RESET, useAtomCallback } from "jotai/utils";
import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router";

const storageOptions = { getOnInit: true };

const guidRegex = /^[a-fA-F\d]{8}-[a-fA-F\d]{4}-[a-fA-F\d]{4}-[a-fA-F\d]{4}-[a-fA-F\d]{12}$/;

const useHostId = () => {
  const { pathname } = useLocation();

  const [_, modules, hostId] = pathname.split("/");

  if (modules !== "modules" || !guidRegex.test(hostId)) throw new Error("Unexpected URL pathname.");

  return hostId;
};

const createKey = (hostId: string, nodeId: number) => `${hostId}_${nodeId}`;

type State = unknown;

type Options = { key: string; defaultState?: unknown; invalidate?: () => void };

const isRecord = (state: State): state is Record<string, unknown> => isPlainObject(state);

const stickyNodeAtoms = atomFamily<Options, WritableAtom<State, State[], void>>(
  ({ key, defaultState, invalidate }: Options) => {
    const storageAtom = atomWithStorage(key, defaultState, undefined, storageOptions);

    // note: a safe-guard until the node sticky state is revisited
    //       perhaps as infrastructure to provide typing, defaults,
    //       and safe access requiring only a node id

    // note: throwing the error outside to prevent issues with tests
    if (defaultState === undefined) invalidate?.();

    return storageAtom;
  },
  (a, b) => a.key === b.key,
);

const useStickyAtom = <T>(nodeId: number, defaultState?: T) => {
  const hostId = useHostId();
  const key = useMemo(() => createKey(hostId, nodeId), [hostId, nodeId]);

  const [valid, setValid] = useState(true);
  const invalidate = useCallback(() => setValid(false), []);

  const options = useMemo<Options>(() => ({ key, defaultState, invalidate }), [key, defaultState, invalidate]);

  const stickyAtom = stickyNodeAtoms(options) as WritableAtom<T, T[], void>;

  const throwException = useCallback(() => {
    stickyNodeAtoms.remove(options);
    throw new Error("Node sticky state must be initialized with a value or null during first access.");
  }, [options]);

  return useMemo(() => ({ stickyAtom, valid, throwException }), [throwException, stickyAtom, valid]);
};

// Note: get and set are two separate hooks for performance reasons.

export const useNodeStickyState = <T extends State>(nodeId: number, defaultState?: T) => {
  const { stickyAtom, valid, throwException } = useStickyAtom<T>(nodeId, defaultState);

  if (!valid) throwException();

  return useAtomValue(stickyAtom);
};

export const useSetNodeStickyState = <T>(nodeId: number) => {
  const { stickyAtom, valid, throwException } = useStickyAtom(nodeId);

  return useAtomCallback<void, Array<Partial<T> | T>>(
    useCallback(
      (get, set, value) => {
        if (!valid) throwException();

        if (value === undefined) return set(stickyAtom, RESET);

        const nodeState = get(stickyAtom);

        const updated = isRecord(value) ? { ...(nodeState ?? {}), ...value } : value;

        return set(stickyAtom, updated);
      },
      [stickyAtom, throwException, valid],
    ),
  );
};

export const useDeleteNodeStickyState = () => {
  const hostId = useHostId();

  return useAtomCallback(
    useCallback(
      (_, set, nodeId: number) => {
        const key = createKey(hostId, nodeId);

        const stateAtom = stickyNodeAtoms({ key });

        set(stateAtom, RESET);

        stickyNodeAtoms.remove({ key });
      },
      [hostId],
    ),
  );
};
